// =============================================================================
// features/sales/campaigns/operate/components/OperateStates.tsx — Estados
// não-"cliente ativo" da tela de operação: carregando, fila vazia, campanha
// não operável, erro (doc 26 §6.8, DoD "fila vazia com estado próprio").
//
// Fila vazia ENSINA o próximo passo (briefing do slot #1) — nunca só "vazio":
// explica os motivos prováveis e dá dois caminhos de saída (atualizar agora
// ou voltar para a lista de campanhas).
// =============================================================================

import * as React from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../../../../components/ui/Button';
import type { CampaignOperableCheck } from '../campaignStatus';

// ---------------------------------------------------------------------------
// Skeleton — carregando o resumo da campanha ou reservando o próximo cliente
// ---------------------------------------------------------------------------

export function OperateSkeleton(): React.JSX.Element {
  return (
    <div
      className="flex flex-col gap-6"
      aria-busy="true"
      aria-label="Carregando operação da campanha"
    >
      <div
        className="h-8 w-64 rounded-sm animate-pulse"
        style={{ background: 'var(--surface-muted)' }}
      />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
        <div
          className="h-64 rounded-md animate-pulse"
          style={{ background: 'var(--surface-muted)' }}
        />
        <div
          className="h-64 rounded-md animate-pulse"
          style={{ background: 'var(--surface-muted)' }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Campanha não operável (rascunho/pronta/pausada/concluída/cancelada)
// ---------------------------------------------------------------------------

export function CampaignNotOperableState({
  check,
}: {
  check: CampaignOperableCheck;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 text-center rounded-md border border-border px-6 py-16"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
    >
      <p
        className="font-display font-bold text-ink"
        style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
      >
        {check.title}
      </p>
      <p className="font-sans text-sm text-ink-3 max-w-sm">{check.description}</p>
      <Link to="/campanhas" className="mt-2">
        <Button type="button" variant="outline">
          Voltar para campanhas
        </Button>
      </Link>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Fila vazia
// ---------------------------------------------------------------------------

export function EmptyQueueState({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  return (
    <div
      className="flex flex-col items-center gap-4 text-center rounded-md border border-border px-6 py-16"
      style={{ background: 'var(--bg-elev-1)', boxShadow: 'var(--elev-2)' }}
    >
      <span
        aria-hidden="true"
        className="w-12 h-12 rounded-pill flex items-center justify-center"
        style={{ background: 'var(--success-bg)', color: 'var(--success)' }}
      >
        <svg
          viewBox="0 0 20 20"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.6}
          className="w-6 h-6"
        >
          <path d="M4 10.5 8 14.5 16 5.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
      <div className="flex flex-col gap-1.5">
        <p
          className="font-display font-bold text-ink"
          style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.03em' }}
        >
          Nenhum cliente disponível agora
        </p>
        <p className="font-sans text-sm text-ink-3 max-w-md">
          Todos os clientes elegíveis já estão reservados por outros vendedores, aguardando retorno
          agendado, ou a carteira desta campanha acabou. Isso muda o tempo todo — tente atualizar em
          alguns minutos, ou volte para acompanhar outra campanha.
        </p>
      </div>
      <div className="flex items-center gap-3 mt-1">
        <Button type="button" variant="primary" onClick={onRetry}>
          Atualizar agora
        </Button>
        <Link to="/campanhas">
          <Button type="button" variant="outline">
            Voltar para campanhas
          </Button>
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Erro ao reservar
// ---------------------------------------------------------------------------

export function OperateErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-4 text-center rounded-md border-l-[3px] px-6 py-16"
      style={{
        borderColor: 'var(--danger)',
        background: 'var(--danger-bg)',
        boxShadow: 'var(--elev-2)',
      }}
    >
      <p className="font-sans font-semibold text-ink">Não foi possível buscar o próximo cliente</p>
      <p className="font-sans text-sm text-ink-2 max-w-md">{message}</p>
      <Button type="button" variant="primary" onClick={onRetry}>
        Tentar novamente
      </Button>
    </div>
  );
}
